import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { MENU_STRUCTURE, CONTACT_INFO } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Luk dropdown når man klikker udenfor
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Luk mobilmenu ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const isLinkActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-brand-brown/10 bg-brand-beige/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/logobilledepng.png"
            alt="Bachmann Tømrer Logo"
            className="h-20 sm:h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {!isMobile && (
          <nav className="flex items-center gap-8" ref={dropdownRef}>
            {MENU_STRUCTURE.map((item, idx) => {
              const hasChildren = item.children && item.children.length > 0;
              const active = isLinkActive(item.href) || (hasChildren && item.children.some(c => isLinkActive(c.href)));

              if (hasChildren) {
                return (
                  <div key={idx} className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                      className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 hover:text-brand-brown py-2 ${
                        active ? "text-brand-brown font-semibold" : "text-brand-graphite/80"
                      }`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${activeDropdown === idx ? "rotate-180" : ""}`} />
                    </button>

                    {activeDropdown === idx && (
                      <div className="absolute left-0 mt-2 w-64 max-h-[70vh] overflow-y-auto rounded-lg border border-brand-brown/10 bg-white p-2 shadow-xl ring-1 ring-black/5 animate-in fade-in slide-in-from-top-2 duration-200 scrollbar-thin">
                        {item.children.map((child, cIdx) => {
                          const childActive = isLinkActive(child.href);
                          return (
                            <Link
                              key={cIdx}
                              to={child.href}
                              onClick={() => setActiveDropdown(null)}
                              className={`block rounded-md px-4 py-2.5 text-xs font-medium transition-colors duration-150 hover:bg-brand-beige hover:text-brand-brown ${
                                childActive ? "bg-brand-beige-dark text-brand-brown font-semibold" : "text-brand-graphite/80"
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-brand-brown py-2 ${
                    active ? "text-brand-brown font-semibold border-b-2 border-brand-brown" : "text-brand-graphite/80"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* CTA Button in Header */}
            <Link
              to="/kontakt"
              className="rounded-full bg-brand-brown px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-white shadow-md transition-all duration-300 hover:bg-brand-brown-dark hover:shadow-lg hover:-translate-y-0.5"
            >
              Få et tilbud
            </Link>
          </nav>
        )}

        {/* Mobile menu button (< 1200px) */}
        {isMobile && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="rounded-lg p-2 text-brand-graphite hover:bg-brand-brown/5 focus:outline-none"
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${isOpen ? "fa-xmark text-2xl" : "fa-bars text-2xl"} text-brand-brown`} />
          </button>
        )}
      </div>

      {/* Mobile Menu (< 1200px) */}
      {isMobile && isOpen && (
        <div className="border-t border-brand-brown/10 bg-brand-beige px-4 py-4 shadow-inner animate-in slide-in-from-top duration-300 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin">
          <div className="space-y-2">
            {MENU_STRUCTURE.map((item, idx) => {
              const hasChildren = item.children && item.children.length > 0;
              const active = isLinkActive(item.href) || (hasChildren && item.children.some(c => isLinkActive(c.href)));

              if (hasChildren) {
                return (
                  <div key={idx} className="space-y-1">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                      className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-brand-brown/5 ${
                        active ? "text-brand-brown font-semibold bg-brand-brown/5" : "text-brand-graphite/80"
                      }`}
                    >
                      {item.label}
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${activeDropdown === idx ? "rotate-180" : ""}`} />
                    </button>

                    {activeDropdown === idx && (
                      <div className="pl-6 space-y-1 border-l border-brand-brown/10 ml-4 my-1">
                        {item.children.map((child, cIdx) => {
                          const childActive = isLinkActive(child.href);
                          return (
                            <Link
                              key={cIdx}
                              to={child.href}
                              className={`block rounded-lg px-4 py-2.5 text-xs font-medium transition-colors hover:bg-brand-brown/5 ${
                                childActive ? "text-brand-brown font-semibold bg-brand-brown/5" : "text-brand-graphite/70"
                              }`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`block rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-brand-brown/5 ${
                    active ? "text-brand-brown font-semibold bg-brand-brown/5" : "text-brand-graphite/80"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* Mobile CTA */}
            <div className="pt-4 border-t border-brand-brown/10 mt-4 flex flex-col gap-3">
              <Link
                to="/kontakt"
                className="w-full text-center rounded-full bg-brand-brown py-3 text-xs font-semibold uppercase tracking-wider text-white shadow-md hover:bg-brand-brown-dark"
              >
                Få et tilbud
              </Link>
              <a
                href={`tel:${CONTACT_INFO.phoneRaw}`}
                className="w-full text-center rounded-full border border-brand-brown/30 py-3 text-xs font-semibold uppercase tracking-wider text-brand-brown hover:bg-brand-brown/5"
              >
                <i className="fa-solid fa-phone mr-2" /> Ring nu
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
